import React, { useEffect, createContext, useReducer, useCallback, useContext, useRef } from 'react';
import { addSingleMarker, clearMarkers, removeSingleMarker, clearColumnMarkers } from '../map_interaction/MarkerUtils';
import { useSyncItinerary } from './useSyncItinerary';
import debounce from 'lodash/debounce';
import useApi from '../../Api';
import { useAuth } from '../../AuthContext';
import { MapContext } from '../maps/MapContext';
import usePlaceDetailsWrapper from '../map_interaction/usePlaceDetailsWrapper';

const createCompositeKey = (place) => `${place.name}_${place.latitude}_${place.longitude}`;

const initialState = {
    savedPlaces: {},
    selectedItinerary: null,
    itineraries: [],
    placeCache: {},
    isPlaceInfoBoxVisible: false,
    placeInfo: null,
    googleMap: null,
    itinerariesLoaded: false,
    currentOriginId: null,
    explore_origin_id: null,
};

const placeReducer = (state, action) => {
    switch (action.type) {
        case 'CLEAR_ITINERARIES':
            return { ...state, itineraries: [] };
        case 'SET_SAVED_PLACES': {
            const { itineraryId, savedPlaces } = action.payload;
            return { ...state, savedPlaces: { ...state.savedPlaces, [itineraryId]: savedPlaces } };
        }
        case 'SET_SELECTED_ITINERARY':
            return { ...state, selectedItinerary: action.payload };
        case 'SET_ITINERARIES':
            return {
                ...state,
                itineraries: [
                    ...state.itineraries,
                    ...action.payload.filter(newItinerary =>
                        !state.itineraries.some(existingItinerary => existingItinerary.id === newItinerary.id)
                    )
                ],
                itinerariesLoaded: true
            };
        case 'REMOVE_ITINERARY':
            return { ...state, itineraries: state.itineraries.filter(itinerary => itinerary.id !== action.payload) };
        case 'CACHE_PLACE_DETAILS':
            return { ...state, placeCache: { ...state.placeCache, [action.payload.origin_id]: action.payload.placeDetails } };
        case 'SET_CURRENT_ORIGIN':
            return { ...state, currentOriginId: action.payload };
        case 'SAVE_PLACE': {
            if (!state.selectedItinerary) return state;
            const newPlace = {
                ...action.payload,
                rating: Number(action.payload.rating),
                notes: action.payload.notes || '',
                date: action.payload.date || null,
                time: action.payload.time || null
            };
            const newSavedPlaces = {
                ...state.savedPlaces,
                [state.selectedItinerary.id]: [
                    ...(state.savedPlaces[state.selectedItinerary.id] || []),
                    newPlace,
                ],
            };
            return { ...state, savedPlaces: newSavedPlaces };
        }
        case 'REMOVE_PLACE_FROM_SAVED': {
            if (!state.selectedItinerary) return state;
            const place = action.payload;
            const updatedSavedPlaces = {
                ...state.savedPlaces,
                [state.selectedItinerary.id]: (state.savedPlaces[state.selectedItinerary.id] || []).filter(
                    (savedPlace) => savedPlace.id !== place.id
                ),
            };
            return { ...state, savedPlaces: updatedSavedPlaces };
        }
        case 'SAVE_ITINERARY_COLUMNS': {
            const updatedItineraries = state.itineraries.map((itinerary) =>
                itinerary.id === state.selectedItinerary.id
                    ? { ...itinerary, columns: action.payload }
                    : itinerary
            );
            return { ...state, itineraries: updatedItineraries, selectedItinerary: { ...state.selectedItinerary, columns: action.payload } };
        }
        case 'UPDATE_PLACE_NOTES': {
            const { placeId, notes } = action.payload;
            const itineraryId = state.selectedItinerary.id;
            const placesWithUpdatedNotes = (state.savedPlaces[itineraryId] || []).map((place) =>
                place.id === placeId ? { ...place, notes } : place
            );
            const updatedColumns = (state.selectedItinerary.columns || []).map((column) => ({
                ...column,
                items: (column.items || []).map((item) =>
                    item.id === placeId ? { ...item, notes } : item
                ),
            }));
            const updatedSelectedItinerary = { ...state.selectedItinerary, columns: updatedColumns };
            const updatedItineraries = state.itineraries.map(itinerary =>
                itinerary.id === itineraryId ? updatedSelectedItinerary : itinerary
            );
            return {
                ...state,
                savedPlaces: { ...state.savedPlaces, [itineraryId]: placesWithUpdatedNotes },
                selectedItinerary: updatedSelectedItinerary,
                itineraries: updatedItineraries
            };
        }
        case 'UPDATE_PLACE_DATE': {
            const { placeId, date } = action.payload;
            const itineraryId = state.selectedItinerary.id;
            const placesWithUpdatedDate = (state.savedPlaces[itineraryId] || []).map((place) =>
                place.id === placeId ? { ...place, date } : place
            );
            const updatedColumns = (state.selectedItinerary.columns || []).map((column) => ({
                ...column,
                items: (column.items || []).map((item) =>
                    item.id === placeId ? { ...item, date } : item
                ),
            }));
            const updatedSelectedItinerary = { ...state.selectedItinerary, columns: updatedColumns };
            const updatedItineraries = state.itineraries.map(itinerary =>
                itinerary.id === itineraryId ? updatedSelectedItinerary : itinerary
            );
            return {
                ...state,
                savedPlaces: { ...state.savedPlaces, [itineraryId]: placesWithUpdatedDate },
                selectedItinerary: updatedSelectedItinerary,
                itineraries: updatedItineraries
            };
        }
        case 'UPDATE_PLACE_TIME': {
            const { placeId, time } = action.payload;
            const itineraryId = state.selectedItinerary.id;
            const placesWithUpdatedTime = (state.savedPlaces[itineraryId] || []).map((place) =>
                place.id === placeId ? { ...place, time } : place
            );
            const updatedColumns = (state.selectedItinerary.columns || []).map((column) => ({
                ...column,
                items: (column.items || []).map((item) =>
                    item.id === placeId ? { ...item, time } : item
                ),
            }));
            const updatedSelectedItinerary = { ...state.selectedItinerary, columns: updatedColumns };
            const updatedItineraries = state.itineraries.map(itinerary =>
                itinerary.id === state.selectedItinerary.id ? updatedSelectedItinerary : itinerary
            );
            return {
                ...state,
                savedPlaces: { ...state.savedPlaces, [itineraryId]: placesWithUpdatedTime },
                selectedItinerary: updatedSelectedItinerary,
                itineraries: updatedItineraries
            };
        }
        case 'UPDATE_PLACE_DETAILS': {
            if (!state.selectedItinerary) return state;
            const itineraryId = state.selectedItinerary.id;
            const updatedPlace = action.payload;
            const updatedSavedPlaces = (state.savedPlaces[itineraryId] || []).map(place =>
                place.id === updatedPlace.id ? updatedPlace : place
            );
            const updatedSelectedItinerary = state.selectedItinerary.columns ? {
                ...state.selectedItinerary,
                columns: (state.selectedItinerary.columns || []).map(column => ({
                    ...column,
                    items: (column.items || []).map(item =>
                        item.id === updatedPlace.id ? updatedPlace : item
                    )
                }))
            } : state.selectedItinerary;
            const updatedItineraries = state.itineraries.map(itinerary =>
                itinerary.id === itineraryId ? updatedSelectedItinerary : itinerary
            );
            return {
                ...state,
                savedPlaces: { ...state.savedPlaces, [itineraryId]: updatedSavedPlaces },
                selectedItinerary: updatedSelectedItinerary,
                itineraries: updatedItineraries,
            };
        }
        case 'SHOW_PLACE_INFO_BOX':
            return { ...state, isPlaceInfoBoxVisible: true, placeInfo: action.payload };
        case 'HIDE_PLACE_INFO_BOX':
            return { ...state, isPlaceInfoBoxVisible: false, placeInfo: null };
        case 'SET_GOOGLE_MAP':
            return { ...state, googleMap: action.payload };
        case 'SET_COLUMN_MARKER_COLOR': {
            const { columnId, color } = action.payload;
            const updatedSelectedItinerary = {
                ...state.selectedItinerary,
                columnMarkerColors: {
                    ...state.selectedItinerary.columnMarkerColors,
                    [columnId]: color
                }
            };
            const updatedItineraries = state.itineraries.map(itinerary =>
                itinerary.id === state.selectedItinerary.id ? updatedSelectedItinerary : itinerary
            );
            return { ...state, selectedItinerary: updatedSelectedItinerary, itineraries: updatedItineraries };
        }
        case 'SET_COLUMN_VISIBILITY': {
            const { columnId, isVisible } = action.payload;
            const updatedSelectedItinerary = {
                ...state.selectedItinerary,
                columnVisibility: {
                    ...state.selectedItinerary.columnVisibility,
                    [columnId]: isVisible
                }
            };
            const updatedItineraries = state.itineraries.map(itinerary =>
                itinerary.id === state.selectedItinerary.id ? updatedSelectedItinerary : itinerary
            );
            return { ...state, selectedItinerary: updatedSelectedItinerary, itineraries: updatedItineraries };
        }
        case 'UPDATE_ITINERARY_DETAILS': {
            const { itineraryId, columnMarkerColors, columnVisibility, columns } = action.payload;
            const updatedItineraries = state.itineraries.map((itinerary) =>
                itinerary.id === itineraryId
                    ? { ...itinerary, columnMarkerColors: { ...columnMarkerColors }, columnVisibility: { ...columnVisibility }, columns: [ ...columns ] }
                    : itinerary
            );
            return { ...state, itineraries: updatedItineraries };
        }
        case 'RENAME_ITINERARY':
            return {
                ...state,
                itineraries: state.itineraries.map(itinerary =>
                    itinerary.id === action.payload.id ? { ...itinerary, name: action.payload.newName } : itinerary
                ),
            };
        case 'SET_EXPLORE_ORIGIN_ID':
            return { ...state, explore_origin_id: action.payload };
        case 'UPDATE_ITINERARY_PRIVACY':
            return {
                ...state,
                itineraries: state.itineraries.map(itinerary =>
                    itinerary.id === action.payload.id ? { ...itinerary, is_public: action.payload.is_public } : itinerary
                ),
                selectedItinerary: state.selectedItinerary && state.selectedItinerary.id === action.payload.id
                    ? { ...state.selectedItinerary, is_public: action.payload.is_public }
                    : state.selectedItinerary,
            };
        case 'ADD_TAG_TO_ITINERARY': {
            const { itineraryId, tag } = action.payload;
            if (!itineraryId) return state;
            let newSelectedItinerary = state.selectedItinerary;
            if (state.selectedItinerary && state.selectedItinerary.id === itineraryId) {
                const currentTags = Array.isArray(state.selectedItinerary.tags) ? state.selectedItinerary.tags : [];
                if (currentTags.some(t => t.name.toLowerCase() === tag.name.toLowerCase())) return state;
                newSelectedItinerary = { ...state.selectedItinerary, tags: [...currentTags, tag] };
            }
            const newItineraries = state.itineraries.map(itinerary => {
                if (itinerary.id === itineraryId) {
                    const currentTags = Array.isArray(itinerary.tags) ? itinerary.tags : [];
                    if (currentTags.some(t => t.name.toLowerCase() === tag.name.toLowerCase())) return itinerary;
                    return { ...itinerary, tags: [...currentTags, tag] };
                }
                return itinerary;
            });
            return { ...state, selectedItinerary: newSelectedItinerary, itineraries: newItineraries };
        }
        case 'REMOVE_TAG_FROM_ITINERARY': {
            const { itineraryId, tagId } = action.payload;
            if (!itineraryId) return state;
            return {
                ...state,
                selectedItinerary: state.selectedItinerary && state.selectedItinerary.id === itineraryId
                    ? { ...state.selectedItinerary, tags: (state.selectedItinerary.tags || []).filter(tag => tag.id !== tagId) }
                    : state.selectedItinerary,
                itineraries: state.itineraries.map(itinerary =>
                    itinerary.id === itineraryId ? { ...itinerary, tags: (itinerary.tags || []).filter(tag => tag.id !== tagId) } : itinerary
                )
            };
        }
        default:
            return state;
    }
};

const PlaceContext = createContext();

export const PlaceProvider = ({ children, readOnly = false }) => {
    const [state, dispatch] = useReducer(placeReducer, initialState);
    const markersRef = useRef(new Map());
    const stateRef = useRef(state);
    const apiClient = useApi();
    const { user, token } = useAuth();
    const { googleMapsAPI } = useContext(MapContext);
    const { syncColumns, syncSavedPlacesColumn, syncSavedPlacesList, syncColumnPlaces, syncItineraryInfo } = useSyncItinerary(stateRef);
    const { fetchPlaceDetailsWrapper } = usePlaceDetailsWrapper();

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    useEffect(() => {
        if (!googleMapsAPI) return;
    }, [googleMapsAPI]);

    const updateItineraryPrivacy = useCallback((id, is_public) => {
        dispatch({ type: 'UPDATE_ITINERARY_PRIVACY', payload: { id, is_public } });
    }, []);

    const clearItineraries = useCallback(() => {
        dispatch({ type: 'CLEAR_ITINERARIES' });
    }, [dispatch]);

    const setCurrentOrigin = useCallback((currentOriginId) => {
        dispatch({ type: 'SET_CURRENT_ORIGIN', payload: currentOriginId });
    }, []);

    const renameItinerary = (id, newName) => {
        dispatch({ type: 'RENAME_ITINERARY', payload: { id, newName } });
    };

    const setSavedPlaces = useCallback((itineraryId, savedPlaces) => {
        dispatch({ type: 'SET_SAVED_PLACES', payload: { itineraryId, savedPlaces } });
    }, []);

    const fetchItineraries = useCallback(async (origin, force = false) => {
        if (!user || !user.emailVerified) return;
        if (!force && stateRef.current.itinerariesLoaded) return;
        try {
            const response = await apiClient.get(`/itineraries/${origin}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            dispatch({ type: 'SET_ITINERARIES', payload: response.data.itineraries });
        } catch (error) {}
    }, [apiClient, user, token]);

    const addTagToItinerary = useCallback(async (tagName) => {
        if (!state.selectedItinerary) return;
        const itineraryId = state.selectedItinerary.id;
        try {
            const response = await apiClient.post(`/itineraries/${itineraryId}/tags`, { name: tagName }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const newTag = response.data;
            dispatch({ type: 'ADD_TAG_TO_ITINERARY', payload: { itineraryId, tag: newTag } });
        } catch (error) {}
    }, [apiClient, state.selectedItinerary, token]);

    const removeTagFromItinerary = useCallback(async (tagId) => {
        if (!state.selectedItinerary) return;
        const itineraryId = state.selectedItinerary.id;
        try {
            await apiClient.delete(`/itineraries/${itineraryId}/tags/${tagId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            dispatch({ type: 'REMOVE_TAG_FROM_ITINERARY', payload: { itineraryId, tagId } });
        } catch (error) {}
    }, [apiClient, state.selectedItinerary, token]);

    const fetchItineraryDetails = useCallback(async (itinerary, options = {}) => {
        const effectiveReadOnly = options.readOnly !== undefined ? options.readOnly : readOnly;
        if (!googleMapsAPI) return;
        try {
            const itineraryId = itinerary.id;
            const endpoint = effectiveReadOnly ? `/itineraries/${itineraryId}/view` : `/itineraries/${itineraryId}/edit`;
            const response = await apiClient.get(endpoint, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const {
                columnMarkerColors,
                columnVisibility,
                columns,
                savedPlaces,
                savedPlacesVisibility,
                savedPlacesColor,
                tags,
                view_count,
                like_count,
                dislike_count,
                author_name,
                updated_datetime_utc,
                user_interaction
            } = response.data;
            columnMarkerColors['saved-places'] = savedPlacesColor;
            columnVisibility['saved-places'] = savedPlacesVisibility;
            const processedColumns = (columns || []).map(column => ({
                ...column,
                date: column.date ? new Date(column.date) : null,
                items: (column.items || []).map(item => {
                    let processedDate = item.date ? new Date(item.date) : null;
                    let processedTime = null;
                    if (item.time && processedDate) {
                        const [hours, minutes, seconds] = item.time.split(':');
                        processedTime = new Date(processedDate);
                        processedTime.setHours(hours, minutes, seconds);
                    }
                    if (!item.google_place_id) {
                        item.__detailsFetched = true;
                    }
                    return { ...item, date: processedDate, time: processedTime };
                })
            }));
            const processedSavedPlaces = (savedPlaces || []).map(place => {
                let processedDate = place.date ? new Date(place.date) : null;
                let processedTime = null;
                if (place.time && processedDate) {
                    const [hours, minutes, seconds] = place.time.split(':');
                    processedTime = new Date(processedDate);
                    processedTime.setHours(hours, minutes, seconds);
                }
                if (!place.google_place_id) {
                    place.__detailsFetched = true;
                }
                return { ...place, date: processedDate, time: processedTime };
            });
            const updatedItinerary = {
                ...itinerary,
                name: response.data.name,
                columnMarkerColors,
                columnVisibility,
                columns: processedColumns,
                tags,
                view_count,
                like_count,
                dislike_count,
                is_owner: response.data.is_owner,
                thumbnail_url: response.data.thumbnail_url,
                author_name,
                updated_datetime_utc,
                user_interaction
            };
            setSavedPlaces(itineraryId, processedSavedPlaces);
            dispatch({
                type: 'UPDATE_ITINERARY_DETAILS',
                payload: { itineraryId, columnMarkerColors, columnVisibility, columns: processedColumns },
            });
            return { itinerary: updatedItinerary, savedPlaces: processedSavedPlaces };
        } catch (error) {
            if (error.response && error.response.status === 403) {
                throw new Error("This itinerary is not public or you do not have permission to view it.");
            }
            if (error.response && error.response.status === 429) {
                throw new Error("Too many requests, please try again later.");
            }
            if (error.response && error.response.status === 404) {
                throw new Error("Itinerary not found");
            } else {
                throw new Error("Error fetching itinerary details");
            }
        }
    }, [
        apiClient,
        dispatch,
        googleMapsAPI,
        token,
        setSavedPlaces,
        readOnly
    ]);

    const handleTagsChange = useCallback((newTags) => {
        if (!state.selectedItinerary) return;
        const currentTags = (state.selectedItinerary.tags || []).map(tag => tag.name);
        const tagsToAdd = newTags.filter(tag => !currentTags.includes(tag));
        const tagsToRemove = currentTags.filter(tag => !newTags.includes(tag));
        tagsToAdd.forEach(tag => addTagToItinerary(tag));
        tagsToRemove.forEach(tag => {
            const tagObj = state.selectedItinerary.tags.find(t => t.name === tag);
            if (tagObj) removeTagFromItinerary(tagObj.id);
        });
    }, [addTagToItinerary, removeTagFromItinerary, state.selectedItinerary]);

    const showPlaceInfoBox = useCallback((placeInfo) => {
        dispatch({ type: 'SHOW_PLACE_INFO_BOX', payload: placeInfo });
    }, []);

    const hidePlaceInfoBox = useCallback(() => {
        dispatch({ type: 'HIDE_PLACE_INFO_BOX' });
    }, []);

    const setGoogleMap = useCallback((googleMap) => {
        dispatch({ type: 'SET_GOOGLE_MAP', payload: googleMap });
    }, []);

    const setSelectedItinerary = useCallback((itinerary, savedPlaces = null) => {
        clearMarkers(markersRef.current);
        if (itinerary === null) {
            dispatch({ type: 'SET_SELECTED_ITINERARY', payload: null });
            return;
        }
        savedPlaces = savedPlaces || stateRef.current.savedPlaces[itinerary.id] || [];
        dispatch({ type: 'SET_SELECTED_ITINERARY', payload: itinerary });
    }, []);

    const setItineraries = useCallback((itineraries) => {
        dispatch({ type: 'SET_ITINERARIES', payload: itineraries });
    }, []);

    const deleteItinerary = useCallback((itineraryId) => {
        dispatch({ type: 'REMOVE_ITINERARY', payload: itineraryId });
    }, []);

    const cachePlaceDetails = useCallback((origin_id, placeDetails) => {
        dispatch({ type: 'CACHE_PLACE_DETAILS', payload: { origin_id, placeDetails } });
    }, []);

    const ensureLatLong = useCallback(async (place) => {
        if (place.__ensuredLatLong) return place;
        if (
            typeof place.latitude === 'number' && !Number.isNaN(place.latitude) &&
            typeof place.longitude === 'number' && !Number.isNaN(place.longitude)
        ) {
            place.__ensuredLatLong = true;
            return place;
        }
        if (!place.google_place_id && !place.place_id) {
            place.__ensuredLatLong = true;
            return place;
        }
        const placeId = place.google_place_id || place.place_id;
        const details = await fetchPlaceDetailsWrapper(placeId);
        if (details && details.latitude && details.longitude) {
            place.latitude = details.latitude;
            place.longitude = details.longitude;
            try {
                await apiClient.post('/places', {
                    place_id: placeId,
                    latitude: details.latitude,
                    longitude: details.longitude,
                });
            } catch (error) {}
        }
        place.__ensuredLatLong = true;
        return place;
    }, [apiClient, fetchPlaceDetailsWrapper]);

    const savePlace = useCallback(async (place) => {
        const columnId = 'saved-places';
        const selectedItinerary = stateRef.current.selectedItinerary;
        const itineraryId = selectedItinerary.id;
        const currentSavedPlaces = stateRef.current.savedPlaces[itineraryId] || [];
        const position = currentSavedPlaces.length;
        const updatedPlace = await ensureLatLong(place);
        const payload = {
            place_id: updatedPlace.place_id || null,
            google_place_id: updatedPlace.google_place_id || null,
            position
        };
        if (!payload.place_id && !payload.google_place_id) {
            throw new Error('Either place_id or google_place_id must be provided');
        }
        await apiClient.post(`/itineraries/${itineraryId}/saved_places/places`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        });
        dispatch({ type: 'SAVE_PLACE', payload: place });
        if (selectedItinerary.columnVisibility[columnId] !== false) {
            const color = selectedItinerary.columnMarkerColors[columnId];
            addSingleMarker(
                googleMapsAPI,
                stateRef.current.googleMap,
                updatedPlace,
                showPlaceInfoBox,
                markersRef,
                color,
                columnId,
                fetchPlaceDetailsWrapper,
                null,
                !updatedPlace.__detailsFetched
            );
        }
    }, [showPlaceInfoBox, apiClient, googleMapsAPI, ensureLatLong, fetchPlaceDetailsWrapper, token]);

    const addMarkers = useCallback(async (places, itinerary = state.selectedItinerary, savedPlacesOverride) => {
        if (!itinerary || !googleMapsAPI) return;
        const savedPlaces = savedPlacesOverride || state.savedPlaces[itinerary.id] || [];
        const addedPlaces = new Set();
        const addMarkerIfNotExists = async (place, color, columnId) => {
            const finalPlace = await ensureLatLong(place);
            const placeKey = createCompositeKey(finalPlace);
            if (!addedPlaces.has(placeKey) && !markersRef.current.has(placeKey)) {
                addSingleMarker(
                    googleMapsAPI,
                    stateRef.current.googleMap,
                    finalPlace,
                    showPlaceInfoBox,
                    markersRef,
                    color,
                    columnId,
                    fetchPlaceDetailsWrapper,
                    null,
                    !finalPlace.__detailsFetched
                );
                addedPlaces.add(placeKey);
            }
        };
        const processPlaces = async (placesArr, color, columnId, isColumnVisible) => {
            if (isColumnVisible !== false) {
                for (const placeItem of placesArr) {
                    await addMarkerIfNotExists(placeItem, color, columnId);
                }
            }
        };
        const cols = itinerary.columns || [];
        for (const column of cols) {
            const placesInColumn = column.items || [];
            const color = itinerary.columnMarkerColors[column.id];
            const isColumnVisible = itinerary.columnVisibility[column.id];
            await processPlaces(placesInColumn, color, column.id, isColumnVisible);
        }
        const savedPlacesColor = itinerary.columnMarkerColors['saved-places'];
        const isSavedPlacesVisible = itinerary.columnVisibility['saved-places'];
        await processPlaces(savedPlaces, savedPlacesColor, 'saved-places', isSavedPlacesVisible);
    }, [showPlaceInfoBox, googleMapsAPI, state.savedPlaces, state.selectedItinerary, ensureLatLong, fetchPlaceDetailsWrapper]);

    const removePlaceFromSaved = useCallback(debounce(async (place) => {
        const itineraryId = stateRef.current.selectedItinerary?.id;
        if (!itineraryId) return;
        const currentSavedPlaces = stateRef.current.savedPlaces[itineraryId] || [];
        const placeIndex = currentSavedPlaces.findIndex(savedPlace => savedPlace.id === place.id);
        const position = placeIndex !== -1 ? placeIndex : null;
        if (position === null) return;
        dispatch({ type: 'REMOVE_PLACE_FROM_SAVED', payload: place });
        const updatedSavedPlaces = currentSavedPlaces.filter(savedPlace => savedPlace.id !== place.id);
        stateRef.current.savedPlaces[itineraryId] = updatedSavedPlaces;
        const placeKey = createCompositeKey(place);
        const marker = markersRef.current.get(placeKey);
        if (marker && marker.columnId === 'saved-places') {
            removeSingleMarker(place, markersRef);
            addMarkers(updatedSavedPlaces);
        }
        const payload = {
            place_id: place.place_id || null,
            google_place_id: place.google_place_id || null,
            position
        };
        await apiClient.delete(`/itineraries/${itineraryId}/saved_places/places`, {
            data: payload
        }, {
            headers: { Authorization: `Bearer ${token}` }
        });
    }, 100), [addMarkers, apiClient, token]);

    const saveItineraryColumns = useCallback((columns) => {
        dispatch({ type: 'SAVE_ITINERARY_COLUMNS', payload: columns });
    }, []);

    const updatePlaceNotes = useCallback((placeId, notes) => {
        dispatch({ type: 'UPDATE_PLACE_NOTES', payload: { placeId, notes } });
    }, []);

    const updatePlaceDate = useCallback((placeId, date) => {
        dispatch({ type: 'UPDATE_PLACE_DATE', payload: { placeId, date } });
    }, []);

    const updatePlaceTime = useCallback((placeId, time) => {
        dispatch({ type: 'UPDATE_PLACE_TIME', payload: { placeId, time } });
    }, []);

    const setColumnMarkerColor = useCallback((columnId, color) => {
        dispatch({ type: 'SET_COLUMN_MARKER_COLOR', payload: { columnId, color } });
        const selectedItinerary = stateRef.current.selectedItinerary;
        if (!selectedItinerary) return;
        let column;
        if (columnId === 'saved-places') {
            column = { id: 'saved-places', items: stateRef.current.savedPlaces[selectedItinerary.id] || [] };
        } else {
            column = selectedItinerary.columns.find(col => col.id === columnId);
        }
        if (!column) return;
        clearColumnMarkers(columnId, markersRef);
        let tempItinerary = { ...selectedItinerary };
        tempItinerary.columnMarkerColors[columnId] = color;
        addMarkers(column.items, tempItinerary);
    }, [addMarkers]);

    const addColumnMarker = useCallback(async (place, columnId) => {
        const selectedItinerary = stateRef.current.selectedItinerary;
        const color = selectedItinerary.columnMarkerColors[columnId];
        const updatedPlace = await ensureLatLong(place);
        if (selectedItinerary.columnVisibility[columnId] !== false) {
            addSingleMarker(
                googleMapsAPI,
                stateRef.current.googleMap,
                updatedPlace,
                showPlaceInfoBox,
                markersRef,
                color,
                columnId,
                fetchPlaceDetailsWrapper,
                null,
                !updatedPlace.__detailsFetched
            );
        }
    }, [showPlaceInfoBox, googleMapsAPI, ensureLatLong, fetchPlaceDetailsWrapper]);

    const removeColumnMarker = useCallback((place) => {
        removeSingleMarker(place, markersRef);
    }, []);

    const deleteColumnAndUpdateMarkers = useCallback((columnId, columns) => {
        const columnToDelete = columns.find(column => column.id === columnId);
        const newColumns = columns.filter(column => column.id !== columnId);
        if (columnToDelete) {
            const selectedItinerary = stateRef.current.selectedItinerary;
            clearColumnMarkers(columnId, markersRef);
            (async () => {
                for (const place of columnToDelete.items || []) {
                    const placeKey = createCompositeKey(place);
                    let placeFoundInOtherColumn = false;
                    if (newColumns && newColumns.length > 0) {
                        for (const column of newColumns) {
                            const columnVisibility = selectedItinerary.columnVisibility[column.id];
                            if (columnVisibility !== false) {
                                if (column.items.some(item => createCompositeKey(item) === placeKey)) {
                                    const color = selectedItinerary.columnMarkerColors[column.id];
                                    const updatedPlace = await ensureLatLong(place);
                                    addSingleMarker(
                                        googleMapsAPI,
                                        stateRef.current.googleMap,
                                        updatedPlace,
                                        showPlaceInfoBox,
                                        markersRef,
                                        color,
                                        column.id,
                                        fetchPlaceDetailsWrapper,
                                        null,
                                        !updatedPlace.__detailsFetched
                                    );
                                    placeFoundInOtherColumn = true;
                                    break;
                                }
                            }
                        }
                    }
                    if (!placeFoundInOtherColumn && columnToDelete.id !== 'saved-places') {
                        const savedPlaces = stateRef.current.savedPlaces[stateRef.current.selectedItinerary.id] || [];
                        const savedPlace = savedPlaces.find(savedPlace => createCompositeKey(savedPlace) === placeKey);
                        if (savedPlace && selectedItinerary.columnVisibility['saved-places'] !== false) {
                            const savedPlacesColor = selectedItinerary.columnMarkerColors['saved-places'];
                            const updatedPlace = await ensureLatLong(savedPlace);
                            addSingleMarker(
                                googleMapsAPI,
                                stateRef.current.googleMap,
                                updatedPlace,
                                showPlaceInfoBox,
                                markersRef,
                                savedPlacesColor,
                                'saved-places',
                                fetchPlaceDetailsWrapper,
                                null,
                                !updatedPlace.__detailsFetched
                            );
                        }
                    }
                }
            })();
        }
        dispatch({ type: 'SAVE_ITINERARY_COLUMNS', payload: newColumns });
        return newColumns;
    }, [
        stateRef,
        markersRef,
        googleMapsAPI,
        showPlaceInfoBox,
        fetchPlaceDetailsWrapper,
        ensureLatLong,
        dispatch
    ]);

    const setColumnVisibility = useCallback((columnId, isVisible) => {
        dispatch({ type: 'SET_COLUMN_VISIBILITY', payload: { columnId, isVisible } });
        const selectedItinerary = stateRef.current.selectedItinerary;
        if (!selectedItinerary) return;
        let column;
        if (columnId === 'saved-places') {
            column = { id: 'saved-places', items: stateRef.current.savedPlaces[selectedItinerary.id] || [] };
        } else {
            column = selectedItinerary.columns.find(col => col.id === columnId);
        }
        if (!column) return;
        if (isVisible) {
            const color = selectedItinerary.columnMarkerColors[columnId];
            (column.items || []).forEach(async (place) => {
                const updatedPlace = await ensureLatLong(place);
                const placeKey = createCompositeKey(updatedPlace);
                const marker = markersRef.current.get(placeKey);
                if (marker) {
                    if (marker.columnId === 'saved-places') {
                        removeSingleMarker(updatedPlace, markersRef);
                        addSingleMarker(
                            googleMapsAPI,
                            stateRef.current.googleMap,
                            updatedPlace,
                            showPlaceInfoBox,
                            markersRef,
                            color,
                            columnId,
                            fetchPlaceDetailsWrapper,
                            null,
                            !updatedPlace.__detailsFetched
                        );
                    }
                } else {
                    addSingleMarker(
                        googleMapsAPI,
                        stateRef.current.googleMap,
                        updatedPlace,
                        showPlaceInfoBox,
                        markersRef,
                        color,
                        columnId,
                        fetchPlaceDetailsWrapper,
                        null,
                        !updatedPlace.__detailsFetched
                    );
                }
            });
        } else {
            clearColumnMarkers(columnId, markersRef);
            let tempItinerary = { ...selectedItinerary };
            const removedPlaces = column.items || [];
            if (columnId !== 'saved-places') {
                tempItinerary.columns = selectedItinerary.columns.filter(col => col.id !== columnId);
                addMarkers(removedPlaces, tempItinerary);
            } else {
                const savedPlaces = [];
                addMarkers(removedPlaces, tempItinerary, savedPlaces);
            }
        }
    }, [showPlaceInfoBox, googleMapsAPI, addMarkers, ensureLatLong, fetchPlaceDetailsWrapper]);

    const setExploreOriginId = useCallback((originId) => {
        dispatch({ type: 'SET_EXPLORE_ORIGIN_ID', payload: originId });
    }, []);

    const saveItinerary = useCallback(async () => {
        if (readOnly) return { success: true };
        try {
            await Promise.all([
                syncColumns(),
                syncSavedPlacesColumn(),
                syncSavedPlacesList(),
                syncColumnPlaces(),
                syncItineraryInfo()
            ]);
            return { success: true };
        } catch (error) {
            return { success: false, error };
        }
    }, [syncColumns, syncSavedPlacesColumn, syncSavedPlacesList, syncColumnPlaces, syncItineraryInfo, readOnly]);

    const updatePlaceDetails = useCallback((updatedPlace) => {
        dispatch({ type: 'UPDATE_PLACE_DETAILS', payload: updatedPlace });
    }, []);

    useEffect(() => {
        if (state.selectedItinerary && state.googleMap) {
            addMarkers(undefined, state.selectedItinerary);
        }
    }, [state.selectedItinerary, state.googleMap, addMarkers]);

    return (
        <PlaceContext.Provider
            value={{
                state,
                stateRef,
                markersRef,
                setGoogleMap,
                setSavedPlaces,
                setSelectedItinerary,
                setItineraries,
                cachePlaceDetails,
                savePlace,
                removePlaceFromSaved,
                saveItineraryColumns,
                updatePlaceNotes,
                updatePlaceDate,
                updatePlaceTime,
                showPlaceInfoBox,
                hidePlaceInfoBox,
                setColumnMarkerColor,
                addColumnMarker,
                removeColumnMarker,
                deleteColumnAndUpdateMarkers,
                setColumnVisibility,
                addMarkers,
                fetchItineraries,
                setCurrentOrigin,
                fetchItineraryDetails,
                clearItineraries,
                renameItinerary,
                setExploreOriginId,
                deleteItinerary,
                updateItineraryPrivacy,
                saveItinerary,
                handleTagsChange,
                updatePlaceDetails,
                readOnly
            }}
        >
            {children}
        </PlaceContext.Provider>
    );
};

export const usePlaceContext = () => useContext(PlaceContext);
export { PlaceContext };
